import axios from 'axios';
import { computed, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import {
  ErrorResponse,
  Loadable,
  SpotMarketRequestTemplate,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { isBadRequest } from '@/util';

import { useSelectedDomain } from './useSelectedDomain';

export interface UseSpotMarketRequestTemplatesResponse {
  createSpotMarketRequestTemplate: (
    template: SpotMarketRequestTemplate,
  ) => Promise<SpotMarketRequestTemplate | undefined>;
  defaultTemplate: Ref<SpotMarketRequestTemplate | undefined>;
  deleteSpotMarketRequestTemplate: (templateId: number) => Promise<boolean>;
  loadSpotMarketRequestTemplates: () => Promise<void>;
  removeDefaultSpotMarketRequestTemplate: (
    template: SpotMarketRequestTemplate,
  ) => Promise<SpotMarketRequestTemplate>;
  setDefaultSpotMarketRequestTemplate: (
    template: SpotMarketRequestTemplate,
  ) => Promise<SpotMarketRequestTemplate>;
  spotMarketRequestTemplates: Ref<Loadable<SpotMarketRequestTemplate[]>>;
  updateSpotMarketRequestTemplate: (
    template: SpotMarketRequestTemplate,
  ) => Promise<SpotMarketRequestTemplate | undefined>;
}

export function useSpotMarketRequestTemplates(): UseSpotMarketRequestTemplatesResponse {
  const { selectedDomain } = useCurrentUserStore();
  const { t } = useI18n();
  const { parentEntity } = useParentEntityStore();
  const watchSelectedDomain = useSelectedDomain();
  const toast = useToast();

  const spotMarketRequestTemplates: Ref<Loadable<SpotMarketRequestTemplate[]>> =
    ref({ loading: false });

  const defaultTemplate = computed(() => {
    return spotMarketRequestTemplates.value.data?.find(
      (t) => t.defaultTemplate,
    );
  });

  const updateExistingTemplateInPlace = (
    updatedTemplate: SpotMarketRequestTemplate,
  ) => {
    const index =
      spotMarketRequestTemplates.value.data?.findIndex(
        (s) => s.id === updatedTemplate.id,
      ) ?? -1;
    if (index !== -1) {
      spotMarketRequestTemplates.value.data?.splice(index, 1, updatedTemplate);
    }
  };

  const createSpotMarketRequestTemplate = async (
    template: SpotMarketRequestTemplate,
  ) => {
    if (!authorization.hasAuthority('spotMarketRequestTemplate', 'create')) {
      return template;
    }

    try {
      spotMarketRequestTemplates.value.loading = true;
      const createdTemplate =
        await api.spotMarketRequestTemplate.create(template);
      spotMarketRequestTemplates.value.data?.push(createdTemplate);

      toast.addSuccess({
        detail: t('messages.successSavingParam', {
          param: t('templates.templates'),
        }),
      });

      return createdTemplate;
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      spotMarketRequestTemplates.value.error = e;
      console.error('Error saving order template', e);

      if (isBadRequest(e)) {
        const errorResponse = e.response?.data as ErrorResponse | undefined;
        toast.addError({
          detail:
            t('errors.savingParam', {
              param: t('templates.templates'),
            }) + getValidationErrors(errorResponse),
          error: e,
        });
      } else {
        toast.addError({
          detail: t('errors.savingParam', {
            param: t('templates.templates'),
          }),
          error: e,
        });
      }
    } finally {
      spotMarketRequestTemplates.value.loading = false;
    }
  };

  const loadSpotMarketRequestTemplates = async () => {
    if (!authorization.hasAuthority('spotMarketRequestTemplate', 'read')) {
      return;
    }

    try {
      spotMarketRequestTemplates.value.loading = true;
      spotMarketRequestTemplates.value.data =
        await api.spotMarketRequestTemplate.findAll();
      watchSelectedDomain(() => loadSpotMarketRequestTemplates());
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      spotMarketRequestTemplates.value.error = e;
      console.error('Error loading order templates', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('templates.templates', 2),
        }),
        error: e,
      });
    } finally {
      spotMarketRequestTemplates.value.loading = false;
    }
  };

  const updateSpotMarketRequestTemplate = async (
    template: SpotMarketRequestTemplate,
  ) => {
    if (!authorization.hasAuthority('spotMarketRequestTemplate', 'update')) {
      return template;
    }

    try {
      spotMarketRequestTemplates.value.loading = true;
      const updatedTemplate =
        await api.spotMarketRequestTemplate.update(template);
      updateExistingTemplateInPlace(updatedTemplate);

      toast.addSuccess({
        detail: t('messages.successUpdatingParam', {
          param: t('templates.templates'),
        }),
      });

      return updatedTemplate;
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      spotMarketRequestTemplates.value.error = e;
      console.error('Error updating order template', e);

      if (isBadRequest(e)) {
        const errorResponse = e.response?.data as ErrorResponse | undefined;
        toast.addError({
          detail:
            t('errors.updatingParam', {
              param: t('templates.templates'),
            }) + getValidationErrors(errorResponse),
          error: e,
        });
      } else {
        toast.addError({
          detail: t('errors.updatingParam', {
            param: t('templates.templates'),
          }),
          error: e,
        });
      }
    } finally {
      spotMarketRequestTemplates.value.loading = false;
    }
  };

  const deleteSpotMarketRequestTemplate = async (
    templateId: number,
  ): Promise<boolean> => {
    if (!authorization.hasAuthority('spotMarketRequestTemplate', 'delete')) {
      return false;
    }

    try {
      spotMarketRequestTemplates.value.loading = true;
      await api.spotMarketRequestTemplate.delete(templateId);
      spotMarketRequestTemplates.value.data =
        spotMarketRequestTemplates.value.data?.filter(
          (s) => s.id !== templateId,
        );

      return true;
    } catch (e) {
      if (axios.isCancel(e)) {
        return false;
      }

      spotMarketRequestTemplates.value.error = e;
      console.error(`Error deleting order template ${templateId}`, e);
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('templates.templates'),
        }),
        error: e,
      });

      return false;
    } finally {
      spotMarketRequestTemplates.value.loading = false;
    }
  };

  const setDefaultSpotMarketRequestTemplate = async (
    template: SpotMarketRequestTemplate,
  ) => {
    let updatedTemplate = template;
    const previousDefaultTemplate = spotMarketRequestTemplates.value.data?.find(
      (t) => t.defaultTemplate,
    );

    try {
      spotMarketRequestTemplates.value.loading = true;
      await api.spotMarketRequestTemplate.setDefault(template.id);
      updatedTemplate = new SpotMarketRequestTemplate({
        ...template,
        defaultTemplate: true,
      });
      updateExistingTemplateInPlace(updatedTemplate);
      if (previousDefaultTemplate) {
        const updatedPreviousDefaultTemplate = new SpotMarketRequestTemplate({
          ...previousDefaultTemplate,
          defaultTemplate: false,
        });
        updateExistingTemplateInPlace(updatedPreviousDefaultTemplate);
      }
      toast.addSuccess({
        detail: t('templates.successSettingDefaultTemplate', {
          templateName: template.name,
          domain: selectedDomain.value?.name,
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      spotMarketRequestTemplates.value.error = e;
      console.error('Error setting default template', e);
      toast.addError({
        detail: t('templates.errors.settingDefaultTemplate'),
        error: e,
      });
    } finally {
      spotMarketRequestTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  const removeDefaultSpotMarketRequestTemplate = async (
    template: SpotMarketRequestTemplate,
  ) => {
    let updatedTemplate = new SpotMarketRequestTemplate({
      ...template,
      defaultTemplate: false,
    });

    try {
      spotMarketRequestTemplates.value.loading = true;
      updatedTemplate =
        await api.spotMarketRequestTemplate.update(updatedTemplate);
      updateExistingTemplateInPlace(updatedTemplate);
      toast.addSuccess({
        detail: t('templates.successRemovingDefaultTemplate', {
          templateName: template.name,
          domain: selectedDomain.value?.name,
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return updatedTemplate;
      }

      spotMarketRequestTemplates.value.error = e;
      console.error('Error removing default order template', e);
      toast.addError({
        detail: t('templates.errors.removingDefaultTemplate'),
        error: e,
      });
    } finally {
      spotMarketRequestTemplates.value.loading = false;
    }

    return updatedTemplate;
  };

  const getValidationErrors = (
    errorResponse: ErrorResponse | undefined,
  ): string => {
    const fieldErrors = errorResponse?.errorDetails?.fieldErrors ?? {};
    const errors = Object.values(fieldErrors).flatMap((val) => {
      if (Array.isArray(val)) {
        return val.map((stringOrFieldError) => {
          if (typeof stringOrFieldError === 'string') {
            return stringOrFieldError;
          } else {
            return stringOrFieldError.message;
          }
        });
      }
      return val.message;
    });

    const exceptionMessage = errorResponse?.exceptionMessage ?? '';
    const errorMessage = exceptionMessage + errors.map((e) => `\r\n- ${e}`);

    return errorMessage ? ` - ${errorMessage}` : '';
  };

  watch(
    () => parentEntity.value?.owner?.id,
    () => {
      spotMarketRequestTemplates.value = { loading: false };
    },
  );

  return {
    createSpotMarketRequestTemplate,
    defaultTemplate,
    deleteSpotMarketRequestTemplate,
    loadSpotMarketRequestTemplates,
    removeDefaultSpotMarketRequestTemplate,
    setDefaultSpotMarketRequestTemplate,
    spotMarketRequestTemplates,
    updateSpotMarketRequestTemplate,
  };
}
